// Canvas toolbar for the Designer (top of the middle column), extracted verbatim
// from Designer.tsx. Presentational: zoom / select-all / border / AI preview /
// 3D / group / undo / redo, all forwarded back to the page via props.
import type { Dispatch, SetStateAction } from 'react'
import type { CatProduct } from '../../lib/catalog-types'
import { AI_TOOLS_ENABLED } from '../../lib/features'

export interface DesignerToolbarProps {
  zoom: number
  setZoom: Dispatch<SetStateAction<number>>
  selectAll: () => void
  borderOn: boolean
  setBorderOn: Dispatch<SetStateAction<boolean>>
  product: CatProduct
  setPreviewOpen: (v: boolean) => void
  setThreeDOpen: (v: boolean) => void
  groupSelected: () => void
  undo: () => void
  redo: () => void
}

export default function DesignerToolbar({
  zoom, setZoom, selectAll, borderOn, setBorderOn, product,
  setPreviewOpen, setThreeDOpen, groupSelected, undo, redo,
}: DesignerToolbarProps) {
  return (
    <div className="px-3 pt-3 pb-2 border-b border-border space-y-1.5">
      <button onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))}
        className="w-full flex items-center justify-between px-2 py-1.5 text-[11px] uppercase tracking-wider font-semibold rounded text-ink-2 hover:bg-surface" title="Zoom in">
        <span>Zoom In</span><span className="text-ink-3">+</span></button>
      <button onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
        className="w-full flex items-center justify-between px-2 py-1.5 text-[11px] uppercase tracking-wider font-semibold rounded text-ink-2 hover:bg-surface" title="Zoom out">
        <span>Zoom Out</span><span className="text-ink-3">−</span></button>
      <button onClick={() => setZoom(1)}
        className="w-full flex items-center justify-between px-2 py-1.5 text-[11px] uppercase tracking-wider font-semibold rounded text-ink-2 hover:bg-surface" title="Reset zoom">
        <span>Fit</span><span className="text-ink-3">{Math.round(zoom * 100)}%</span></button>
      <div className="border-t border-border my-1.5" />
      <button onClick={selectAll}
        className="w-full px-2 py-1.5 text-[11px] uppercase tracking-wider font-semibold rounded text-ink-2 hover:bg-surface text-left" title="Select all">Select All</button>
      <button onClick={() => setBorderOn((b) => !b)}
        className={'w-full flex items-center justify-between px-2 py-1.5 text-[11px] uppercase tracking-wider font-semibold rounded text-left ' + (borderOn ? 'text-gold-dark bg-gold/10' : 'text-ink-2 hover:bg-surface')}
        title="Toggle decoration area border">
        <span>Border</span><span>{borderOn ? 'ON' : 'OFF'}</span></button>
      {AI_TOOLS_ENABLED && product?.aiPreviewEnabled && (
        <button onClick={() => setPreviewOpen(true)}
          className="w-full flex items-center justify-between px-2 py-1.5 text-[11px] uppercase tracking-wider font-semibold rounded text-left text-emerald-700 hover:bg-emerald-50"
          title="Generate a realistic AI preview of this design on the garment">
          <span>Preview</span><span>✨</span></button>
      )}
      <button onClick={groupSelected}
        className="w-full px-2 py-1.5 text-[11px] uppercase tracking-wider font-semibold rounded text-ink-2 hover:bg-surface text-left" title="Group / Ungroup">Group</button>
      <button onClick={undo}
        className="w-full flex items-center justify-between px-2 py-1.5 text-[11px] uppercase tracking-wider font-semibold rounded text-ink-2 hover:bg-surface" title="Undo">
        <span>Undo</span><span className="text-ink-3">↶</span></button>
      <button onClick={redo}
        className="w-full flex items-center justify-between px-2 py-1.5 text-[11px] uppercase tracking-wider font-semibold rounded text-ink-2 hover:bg-surface" title="Redo">
        <span>Redo</span><span className="text-ink-3">↷</span></button>
    </div>
  )
}
